import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldAlert,
  AlertTriangle,
  Info,
  Play,
  Loader2,
  CheckCircle2,
  Search,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Plus,
  Globe,
  Zap,
  Eye,
  RefreshCw,
  Wand2,
  Upload,
  X,
} from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import * as api from "../lib/api";

const PRIMARY = "#018D4C";
const SEVERITY_ORDER = { critical: 0, warning: 1, info: 2 };

const SEVERITY_META = {
  critical: { color: "#DC2626", bg: "#FEF2F2", label: "Critical" },
  warning:  { color: "#F59E0B", bg: "#FFFBEB", label: "Warning" },
  info:     { color: "#2563EB", bg: "#EFF6FF", label: "Info" },
};

const ISSUE_TYPE_LABELS = {
  broken_link: "Broken Link",
  redirect_chain: "Redirect Chain",
  noindex: "Noindex Tag",
  canonical_conflict: "Canonical Conflict",
  missing_canonical: "Missing Canonical",
  duplicate_title: "Duplicate Title",
  missing_meta_desc: "Missing Meta Description",
  orphan_page: "Orphan Page",
  deep_click_depth: "Deep Click Depth",
  oversized_page: "Oversized Page",
  missing_h1: "Missing H1",
  no_schema: "No Structured Data",
  soft_404: "Soft 404",
  google_fetch_error: "Google Fetch Error",
  not_indexed: "Not Indexed",
  discovered_not_indexed: "Discovered – Not Indexed",
  crawled_not_indexed: "Crawled – Not Indexed",
  not_found_404: "Not Found (404)",
  excluded_by_noindex: "Excluded by Noindex",
  duplicate_no_canonical: "Duplicate, No Canonical",
  alternate_canonical: "Alternate Canonical",
  page_with_redirect: "Page with Redirect",
  unknown_to_google: "Unknown to Google",
  google_canonical_override: "Google Canonical Override",
  robots_blocked_by_google: "Blocked by Robots",
  rich_result_error: "Rich Result Error",
  not_in_sitemap: "Not in Sitemap",
  not_mobile_first: "Not Mobile-First",
  stale_google_crawl: "Stale Google Crawl",
};

// ── GSC Sitemaps Panel ────────────────────────────────────────────────────────

function GscSitemapsPanel() {
  const [open, setOpen] = useState(true);
  const [sitemaps, setSitemaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitUrl, setSubmitUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fetchSitemaps = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getGscSitemaps();
      setSitemaps(res.sitemaps || []);
      setError(null);
    } catch {
      setError("GSC not configured or sitemaps unavailable.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSitemaps(); }, [fetchSitemaps]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!submitUrl.trim()) return;
    setSubmitting(true);
    try {
      await api.submitGscSitemap(submitUrl.trim());
      setSubmitUrl("");
      await fetchSitemaps();
    } catch {
      // silently fail — may not be wired yet
    } finally {
      setSubmitting(false);
    }
  };

  const rowClass = (sm) => {
    if (sm.is_pending) return "border-l-gray-300 bg-gray-50";
    if (sm.errors > 0) return "border-l-red-400 bg-red-50";
    if (sm.warnings > 0) return "border-l-amber-400 bg-amber-50";
    return "border-l-green-400 bg-green-50";
  };

  const StatusIcon = ({ sm }) => {
    if (sm.is_pending) return <Loader2 size={12} className="text-gray-400 animate-spin flex-shrink-0" />;
    if (sm.errors > 0) return <ShieldAlert size={12} className="text-red-500 flex-shrink-0" />;
    if (sm.warnings > 0) return <AlertTriangle size={12} className="text-amber-500 flex-shrink-0" />;
    return <CheckCircle2 size={12} className="text-green-600 flex-shrink-0" />;
  };

  return (
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Globe size={15} className="text-[#018D4C]" />
          <span className="font-semibold text-sm">GSC Sitemaps</span>
          {!loading && !error && (
            <span className="text-xs text-muted-foreground">({sitemaps.length})</span>
          )}
        </div>
        {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
      </button>

      {open && (
        <div className="border-t">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground text-sm gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading sitemaps…
            </div>
          ) : error ? (
            <div className="px-5 py-4 text-sm text-muted-foreground">{error}</div>
          ) : (
            <>
              <div className="divide-y">
                {sitemaps.length === 0 ? (
                  <div className="px-5 py-4 text-sm text-muted-foreground">No sitemaps submitted yet.</div>
                ) : (
                  sitemaps.map((sm) => (
                    <div key={sm.path} className={`flex items-center gap-3 px-5 py-3 border-l-4 ${rowClass(sm)}`}>
                      <StatusIcon sm={sm} />
                      <span className="text-xs font-mono flex-1 truncate">{sm.path}</span>
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground px-1.5 py-0.5 bg-white border rounded">
                        {sm.type}
                      </span>
                      {sm.errors > 0 && (
                        <span className="text-[10px] font-semibold text-red-600">{sm.errors} err</span>
                      )}
                      {sm.warnings > 0 && (
                        <span className="text-[10px] font-semibold text-amber-600">{sm.warnings} warn</span>
                      )}
                      {sm.last_downloaded && (
                        <span className="text-[10px] text-muted-foreground hidden md:block">
                          crawled {new Date(sm.last_downloaded).toLocaleDateString()}
                        </span>
                      )}
                      {sm.content_types?.length > 0 && (
                        <div className="hidden lg:flex gap-1">
                          {sm.content_types.map((ct, i) => {
                            const label = typeof ct === "object" && ct !== null ? ct.type : ct;
                            return (
                              <span key={i} className="text-[9px] uppercase tracking-wider text-muted-foreground">
                                {label}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
              <form onSubmit={handleSubmit} className="flex items-center gap-2 px-5 py-3 border-t bg-muted/20">
                <input
                  value={submitUrl}
                  onChange={(e) => setSubmitUrl(e.target.value)}
                  placeholder="https://example.com/sitemap.xml"
                  className="flex-1 text-sm border rounded-md px-3 py-1.5 bg-background focus:outline-none focus:ring-1 focus:ring-[#018D4C]"
                />
                <button
                  type="submit"
                  disabled={submitting || !submitUrl.trim()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white rounded-md disabled:opacity-60 transition-opacity"
                  style={{ background: PRIMARY }}
                >
                  {submitting ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
                  Submit
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Health Score ──────────────────────────────────────────────────────────────

function HealthScore({ score }) {
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 80 ? PRIMARY : score >= 50 ? "#F59E0B" : "#DC2626";

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="relative w-32 h-32">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r={radius} stroke="#E5E7EB" strokeWidth="10" fill="none" />
          <circle
            cx="60" cy="60"
            r={radius}
            stroke={color}
            strokeWidth="10"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold" style={{ color }}>{score}</span>
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Health</span>
        </div>
      </div>
    </div>
  );
}

// ── Issue Distribution ────────────────────────────────────────────────────────

function IssueDistribution({ issues, sourceFilter, onSourceChange }) {
  const filtered = sourceFilter === "all"
    ? issues
    : issues.filter((i) => (i.source || "crawler") === sourceFilter);

  const counts = {
    critical: filtered.filter((i) => i.severity === "critical").length,
    warning: filtered.filter((i) => i.severity === "warning").length,
    info: filtered.filter((i) => i.severity === "info").length,
  };

  const data = [
    { name: "Critical", value: counts.critical, color: SEVERITY_META.critical.color },
    { name: "Warning",  value: counts.warning,  color: SEVERITY_META.warning.color },
    { name: "Info",     value: counts.info,      color: SEVERITY_META.info.color },
  ].filter((d) => d.value > 0);

  const tabs = [
    { key: "all",     label: "All" },
    { key: "crawler", label: "Crawler" },
    { key: "gsc",     label: "GSC" },
  ];

  return (
    <div>
      <div className="flex items-center gap-1 mb-3">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => onSourceChange(t.key)}
            className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors ${
              sourceFilter === t.key
                ? "bg-[#018D4C] text-white"
                : "text-muted-foreground hover:bg-muted"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {data.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
          No issues — run an audit.
        </div>
      ) : (
        <div className="h-40">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={44}
                outerRadius={62}
                paddingAngle={4}
                dataKey="value"
                stroke="none"
              >
                {data.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-4 mt-1">
            {data.map((d) => (
              <div key={d.name} className="flex items-center gap-1.5 text-xs">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }} />
                <span className="font-medium">{d.name}</span>
                <span className="text-muted-foreground">({d.value})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── GSC Enrichment Panel ──────────────────────────────────────────────────────

// Rows mirror Search Console's "Why pages aren't indexed" report, then the extra checks.
const GSC_REASON_ROWS = [
  { type: "discovered_not_indexed",    label: "Discovered – currently not indexed" },
  { type: "crawled_not_indexed",       label: "Crawled – currently not indexed" },
  { type: "not_found_404",             label: "Not found (404)" },
  { type: "soft_404",                  label: "Soft 404" },
  { type: "robots_blocked_by_google",  label: "Blocked by robots.txt" },
  { type: "excluded_by_noindex",       label: "Excluded by noindex" },
  { type: "google_canonical_override", label: "Google chose different canonical" },
  { type: "duplicate_no_canonical",    label: "Duplicate without canonical" },
  { type: "alternate_canonical",       label: "Alternate page with proper canonical" },
  { type: "page_with_redirect",        label: "Page with redirect" },
  { type: "google_fetch_error",        label: "Fetch error (5xx / 401 / 403 / redirect)" },
  { type: "unknown_to_google",         label: "URL is unknown to Google" },
  { type: "not_indexed",               label: "Not indexed (other)" },
];
const GSC_CHECK_ROWS = [
  { type: "rich_result_error",  label: "Rich result errors" },
  { type: "not_mobile_first",   label: "Not mobile-first" },
  { type: "not_in_sitemap",     label: "Indexed, not in sitemap" },
  { type: "stale_google_crawl", label: "Not crawled in 90+ days" },
];

function GscEnrichmentPanel({ summary, running, onFilterByType, onEnrich }) {
  if (!summary) return null;
  const counts = summary.gsc_issue_counts || {};
  const reasonRows = GSC_REASON_ROWS.filter((r) => counts[r.type] > 0);
  const checkRows = GSC_CHECK_ROWS.filter((r) => counts[r.type] > 0);

  const Row = ({ r }) => {
    const sev = ISSUE_SEVERITY_HINT[r.type] || "warning";
    return (
      <button
        onClick={() => onFilterByType(r.type)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-muted/40 transition-colors text-left"
      >
        <span className="text-sm">{r.label}</span>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold" style={{ color: SEVERITY_META[sev].color }}>
            {counts[r.type]} pages
          </span>
          <ExternalLink size={10} className="text-muted-foreground" />
        </div>
      </button>
    );
  };

  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Eye size={14} className="text-[#018D4C]" />
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Google's View — Page Indexing
          </span>
        </div>
        <div className="flex items-center gap-3">
          {summary.last_enriched_at && (
            <span className="text-[10px] text-muted-foreground">
              Inspected {new Date(summary.last_enriched_at).toLocaleDateString()}
            </span>
          )}
          <button
            onClick={onEnrich}
            disabled={running}
            className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded border hover:bg-[#018D4C]/10 hover:border-[#018D4C] transition-colors disabled:opacity-60"
          >
            <RefreshCw size={10} className={running ? "animate-spin" : ""} />
            {summary.gsc_enriched ? "Re-inspect" : "Inspect with Google"}
          </button>
        </div>
      </div>

      {!summary.gsc_enriched ? (
        <div className="text-sm text-muted-foreground py-2">
          Ask Google which crawled pages are indexed and why the rest aren't. Uses the URL Inspection API
          (up to 500 URLs per run; requires Search Console in Settings).
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="rounded-lg px-3 py-2 bg-gray-500 text-white">
              <div className="text-[10px] uppercase tracking-wider opacity-80">Not indexed</div>
              <div className="text-2xl font-bold">{summary.gsc_not_indexed_count ?? 0}</div>
            </div>
            <div className="rounded-lg px-3 py-2 text-white" style={{ background: PRIMARY }}>
              <div className="text-[10px] uppercase tracking-wider opacity-80">Indexed</div>
              <div className="text-2xl font-bold">{summary.gsc_indexed_count ?? 0}</div>
            </div>
          </div>
          {reasonRows.length === 0 && checkRows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-2">No GSC issues detected.</div>
          ) : (
            <div className="space-y-3">
              {reasonRows.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-3 mb-1">
                    Why pages aren't indexed
                  </div>
                  <div className="space-y-0.5">{reasonRows.map((r) => <Row key={r.type} r={r} />)}</div>
                </div>
              )}
              {checkRows.length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-3 mb-1">
                    Other Google checks
                  </div>
                  <div className="space-y-0.5">{checkRows.map((r) => <Row key={r.type} r={r} />)}</div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Display color per GSC type; matches the severities assigned in backend/services/audit_gsc.py.
const ISSUE_SEVERITY_HINT = {
  crawled_not_indexed: "critical",
  not_found_404: "critical",
  soft_404: "critical",
  robots_blocked_by_google: "critical",
  excluded_by_noindex: "critical",
  google_canonical_override: "critical",
  google_fetch_error: "critical",
  alternate_canonical: "info",
  page_with_redirect: "info",
  not_in_sitemap: "info",
  stale_google_crawl: "info",
};

// ── Top Priorities ────────────────────────────────────────────────────────────

function TopPriorities({ issues }) {
  const top = [...issues]
    .filter((i) => i.severity === "critical" && (i.ai_explanation || i.recommendation))
    .sort((a, b) => {
      const aGsc = (a.source || "crawler") === "gsc" ? 0 : 1;
      const bGsc = (b.source || "crawler") === "gsc" ? 0 : 1;
      return aGsc - bGsc;
    })
    .slice(0, 5);

  if (top.length === 0) return null;

  return (
    <div className="bg-white border rounded-xl p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Zap size={14} className="text-[#018D4C]" />
        <h2 className="font-semibold text-sm">Top Priorities</h2>
        <span className="text-xs text-muted-foreground">AI-ranked critical issues</span>
      </div>
      <div className="space-y-4">
        {top.map((issue, i) => (
          <div key={issue.id} className="flex gap-3">
            <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 text-red-600 text-[10px] font-bold flex items-center justify-center mt-0.5">
              {i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-semibold">
                  {ISSUE_TYPE_LABELS[issue.issue_type] || issue.issue_type}
                </span>
                {(issue.source || "crawler") === "gsc" && (
                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded font-semibold">
                    GSC
                  </span>
                )}
              </div>
              <div className="text-[11px] text-muted-foreground truncate">
                {issue.page_title || issue.page_url}
              </div>
              {issue.ai_explanation ? (
                <div className="text-xs mt-1 text-foreground/75 italic">{issue.ai_explanation}</div>
              ) : issue.recommendation ? (
                <div className="text-xs mt-1 text-foreground/75">{issue.recommendation}</div>
              ) : null}
              {issue.ai_business_impact && (
                <div className="text-[11px] mt-0.5 text-amber-700">{issue.ai_business_impact}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const REWRITABLE_TYPES = new Set(["duplicate_title", "missing_meta_desc", "missing_h1"]);
const FIELD_LABELS = { title: "Title", meta_desc: "Meta Description", h1: "H1" };

// ── Fix Panel ─────────────────────────────────────────────────────────────────

function FixPanel({ issue, onClose, onResolved }) {
  const [suggestion, setSuggestion] = useState("");
  const [field, setField] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    setGenerating(true);
    setResult(null);
    try {
      const res = await api.rewriteAuditIssue(issue.id);
      setSuggestion(res.suggestion || "");
      setField(res.field || null);
    } catch {
      setResult({ success: false, message: "Failed to generate rewrite." });
    } finally {
      setGenerating(false);
    }
  };

  const apply = async () => {
    if (!suggestion.trim()) return;
    setApplying(true);
    setResult(null);
    try {
      const res = await api.applyAuditFix(issue.id, suggestion.trim());
      setResult(res);
      if (res.success) onResolved(issue.id);
    } catch (e) {
      setResult({ success: false, message: e?.response?.data?.detail || "Apply failed." });
    } finally {
      setApplying(false);
    }
  };

  const canRewrite = REWRITABLE_TYPES.has(issue.issue_type);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2">
            <Wand2 size={15} className="text-[#018D4C]" />
            <h3 className="font-semibold text-sm">Fix: {ISSUE_TYPE_LABELS[issue.issue_type] || issue.issue_type}</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          {/* Recommendation */}
          {(issue.ai_explanation || issue.recommendation) && (
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-1">
              {issue.ai_explanation && (
                <p className="text-xs text-blue-800 italic">{issue.ai_explanation}</p>
              )}
              {issue.recommendation && (
                <p className="text-xs text-blue-700">{issue.recommendation}</p>
              )}
              {issue.ai_business_impact && (
                <p className="text-[11px] text-amber-700 mt-1">{issue.ai_business_impact}</p>
              )}
            </div>
          )}

          {/* Rewrite section */}
          {canRewrite && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {field ? `AI-Generated ${FIELD_LABELS[field]}` : "AI Rewrite"}
                </span>
                <button
                  onClick={generate}
                  disabled={generating}
                  className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded border hover:bg-[#018D4C]/10 hover:border-[#018D4C] transition-colors disabled:opacity-60"
                >
                  {generating ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                  {suggestion ? "Regenerate" : "Generate"}
                </button>
              </div>
              {suggestion && (
                <textarea
                  value={suggestion}
                  onChange={(e) => setSuggestion(e.target.value)}
                  rows={3}
                  className="w-full text-sm border rounded-lg px-3 py-2 bg-background focus:outline-none focus:ring-1 focus:ring-[#018D4C] resize-none"
                />
              )}
            </div>
          )}

          {/* Result banner */}
          {result && (
            <div className={`text-xs px-3 py-2 rounded-lg ${result.success ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
              {result.success ? "Applied successfully." : result.message}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t bg-muted/20">
          <div className="text-[11px] text-muted-foreground truncate max-w-[220px]">
            {issue.page_url}
          </div>
          <div className="flex items-center gap-2">
            {canRewrite && suggestion && issue.wp_post_id && (
              <button
                onClick={apply}
                disabled={applying || result?.success}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-md disabled:opacity-60 transition-opacity"
                style={{ background: PRIMARY }}
              >
                {applying ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                Apply Fix
              </button>
            )}
            {canRewrite && suggestion && !issue.wp_post_id && (
              <span className="text-[10px] text-amber-600">No live post linked — copy manually</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Issue Table ───────────────────────────────────────────────────────────────

function IssueTable({ issues, onResolve, onOpenDetail, sourceFilter, externalTypeFilter }) {
  const [filterSeverity, setFilterSeverity] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "severity", asc: true });
  const [fixIssue, setFixIssue] = useState(null);

  useEffect(() => {
    if (externalTypeFilter) setFilterType(externalTypeFilter);
  }, [externalTypeFilter]);

  const sourceFiltered = sourceFilter === "all"
    ? issues
    : issues.filter((i) => (i.source || "crawler") === sourceFilter);

  const filtered = sourceFiltered.filter((i) => {
    if (filterSeverity !== "all" && i.severity !== filterSeverity) return false;
    if (filterType !== "all" && i.issue_type !== filterType) return false;
    const q = search.toLowerCase();
    return (
      (i.page_url || "").toLowerCase().includes(q) ||
      (i.description || "").toLowerCase().includes(q) ||
      (i.issue_type || "").toLowerCase().includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sort.key === "severity") {
      cmp = (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99);
    } else if (sort.key === "issue_type") {
      cmp = (ISSUE_TYPE_LABELS[a.issue_type] || a.issue_type).localeCompare(
        ISSUE_TYPE_LABELS[b.issue_type] || b.issue_type
      );
    } else {
      cmp = (a[sort.key] || "").localeCompare(b[sort.key] || "");
    }
    return sort.asc ? cmp : -cmp;
  });

  const allTypes = Array.from(new Set(issues.map((i) => i.issue_type)));

  const SortHeader = ({ label, colKey }) => (
    <button
      onClick={() => setSort({ key: colKey, asc: sort.key === colKey ? !sort.asc : true })}
      className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground"
    >
      {label}
      {sort.key === colKey && (
        <ChevronDown size={12} className={sort.asc ? "" : "rotate-180"} />
      )}
    </button>
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search issues…"
            className="w-full pl-8 pr-3 py-1.5 text-sm border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-[#018D4C]"
          />
        </div>
        <select
          value={filterSeverity}
          onChange={(e) => setFilterSeverity(e.target.value)}
          className="text-sm border rounded-md px-2 py-1.5 bg-background"
        >
          <option value="all">All Severities</option>
          <option value="critical">Critical</option>
          <option value="warning">Warning</option>
          <option value="info">Info</option>
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="text-sm border rounded-md px-2 py-1.5 bg-background"
        >
          <option value="all">All Types</option>
          {allTypes.map((t) => (
            <option key={t} value={t}>{ISSUE_TYPE_LABELS[t] || t}</option>
          ))}
        </select>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="px-3 py-2"><SortHeader label="Severity" colKey="severity" /></th>
              <th className="px-3 py-2"><SortHeader label="Type" colKey="issue_type" /></th>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Source
              </th>
              <th className="px-3 py-2">Page</th>
              <th className="px-3 py-2">Description</th>
              <th className="px-3 py-2"><SortHeader label="Detected" colKey="detected_at" /></th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {sorted.map((issue) => {
              const sm = SEVERITY_META[issue.severity] || SEVERITY_META.info;
              const isGsc = (issue.source || "crawler") === "gsc";
              return (
                <tr key={issue.id} className="hover:bg-muted/30 transition-colors">
                  <td className="px-3 py-2.5">
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                      style={{ background: sm.bg, color: sm.color }}
                    >
                      {issue.severity === "critical" && <ShieldAlert size={10} />}
                      {issue.severity === "warning" && <AlertTriangle size={10} />}
                      {issue.severity === "info" && <Info size={10} />}
                      {sm.label}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-xs font-medium text-muted-foreground">
                    {ISSUE_TYPE_LABELS[issue.issue_type] || issue.issue_type}
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className={`text-[9px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
                        isGsc
                          ? "bg-blue-50 text-blue-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {isGsc ? "GSC" : "Crawler"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 max-w-[200px]">
                    <button
                      onClick={() => onOpenDetail(issue.page_id)}
                      className="text-xs text-[#018D4C] hover:underline truncate flex items-center gap-1"
                    >
                      <span className="truncate">{issue.page_title || issue.page_url}</span>
                      <ExternalLink size={10} />
                    </button>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground max-w-xs truncate">
                    {issue.description}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground whitespace-nowrap">
                    {issue.detected_at ? new Date(issue.detected_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {REWRITABLE_TYPES.has(issue.issue_type) && (
                        <button
                          onClick={() => setFixIssue(issue)}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded border border-[#018D4C]/30 text-[#018D4C] hover:bg-[#018D4C]/10 transition-colors"
                        >
                          <Wand2 size={10} />
                          Fix
                        </button>
                      )}
                      {!REWRITABLE_TYPES.has(issue.issue_type) && (issue.recommendation || issue.ai_explanation) && (
                        <button
                          onClick={() => setFixIssue(issue)}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded border hover:bg-muted transition-colors"
                          style={{ borderColor: "#E5E7EB" }}
                        >
                          <Info size={10} />
                          View
                        </button>
                      )}
                      <button
                        onClick={() => onResolve(issue.id)}
                        className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded border hover:bg-[#018D4C]/10 hover:border-[#018D4C] transition-colors"
                        style={{ borderColor: "#E5E7EB" }}
                      >
                        <CheckCircle2 size={10} />
                        Resolve
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {sorted.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No issues match your filters.
          </div>
        )}
      </div>

      {fixIssue && (
        <FixPanel
          issue={fixIssue}
          onClose={() => setFixIssue(null)}
          onResolved={(id) => { setFixIssue(null); onResolve(id); }}
        />
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function TechnicalAuditPage() {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [issues, setIssues] = useState([]);
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState(null);
  const issueTableRef = useRef(null);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.getAuditSummary();
      setSummary(res);
    } catch (e) {
      console.error("[audit] summary fetch failed", e);
    }
  }, []);

  const fetchIssues = useCallback(async () => {
    try {
      const res = await api.getAuditIssues();
      setIssues(res.issues || []);
    } catch (e) {
      console.error("[audit] issues fetch failed", e);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.getAuditStatus();
      setStatus(res);
      return res;
    } catch (e) {
      console.error("[audit] status fetch failed", e);
      return null;
    }
  }, []);

  // Crawl and GSC inspection share one job state; a crawl chains into inspection when
  // GSC is configured (`chain_pending`), so don't stop at the crawl's own "completed".
  const pollUntilDone = () => {
    const poll = setInterval(async () => {
      const s = await fetchStatus();
      if (s && !s.chain_pending && (s.status === "completed" || s.status === "error")) {
        clearInterval(poll);
        setRunning(false);
        await fetchSummary();
        await fetchIssues();
      }
    }, 2500);
    setTimeout(() => { clearInterval(poll); setRunning(false); }, 900000);
  };

  const startAudit = async () => {
    setRunning(true);
    try {
      await api.startAudit();
      pollUntilDone();
    } catch (e) {
      setRunning(false);
      console.error("[audit] start failed", e);
    }
  };

  const resolveIssue = async (id) => {
    try {
      await api.resolveAuditIssue(id);
      await fetchIssues();
      await fetchSummary();
    } catch (e) {
      console.error("[audit] resolve failed", e);
    }
  };

  const triggerEnrich = async () => {
    setRunning(true);
    try {
      await api.triggerAuditEnrich();
      pollUntilDone();
    } catch (e) {
      setRunning(false);
      console.error("[audit] enrich failed", e);
      setStatus({ status: "error", error: e?.response?.data?.detail || "GSC inspection failed to start." });
    }
  };

  const handleFilterByType = (type) => {
    setTypeFilter(type);
    issueTableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  useEffect(() => {
    fetchSummary();
    fetchIssues();
    fetchStatus();
  }, [fetchSummary, fetchIssues, fetchStatus]);

  const healthScore = summary
    ? Math.max(0, Math.min(100, 100 - (summary.critical_count ?? 0) * 10 - (summary.warning_count ?? 0) * 3 - (summary.info_count ?? 0) * 1))
    : 100;

  return (
    <div className="space-y-6 p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Technical SEO Audit</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Crawl your site, detect issues, and prioritize fixes.
          </p>
        </div>
        <button
          onClick={startAudit}
          disabled={running}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold text-white transition-colors disabled:opacity-60"
          style={{ background: PRIMARY }}
        >
          {running ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
          {running ? "Running Audit…" : "Run Audit"}
        </button>
      </div>

      {/* Progress banner */}
      {(status?.status === "running" || status?.chain_pending) && (
        <div className="flex items-center gap-3 text-sm bg-[#E6F5EE] text-[#01723D] px-4 py-3 rounded-lg border border-[#018D4C]/20">
          <Loader2 size={16} className="animate-spin" />
          <span>
            {status.kind === "enrich"
              ? `Inspected ${status.crawled ?? 0}/${status.total ?? "?"} URLs with Google… `
              : `Crawled ${status.crawled ?? 0} pages… `}
            {status.message || ""}
          </span>
        </div>
      )}

      {/* Phase 0 — GSC Sitemaps */}
      <GscSitemapsPanel />

      {/* Health + Issue Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-1 bg-white border rounded-xl p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
            Health Score
          </div>
          <HealthScore score={healthScore} />
          {summary && (
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              {[
                { label: "Critical", count: summary.critical_count, color: "#DC2626" },
                { label: "Warning",  count: summary.warning_count,  color: "#F59E0B" },
                { label: "Info",     count: summary.info_count,     color: "#2563EB" },
              ].map(({ label, count, color }) => (
                <div key={label}>
                  <div className="text-lg font-bold" style={{ color }}>{count ?? 0}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="md:col-span-2 bg-white border rounded-xl p-5 shadow-sm">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
            Issue Distribution
          </div>
          <IssueDistribution
            issues={issues}
            sourceFilter={sourceFilter}
            onSourceChange={setSourceFilter}
          />
        </div>
      </div>

      {status?.status === "error" && status.error && (
        <div className="text-sm bg-red-50 text-red-700 px-4 py-3 rounded-lg border border-red-200">
          {status.error}
        </div>
      )}

      {/* Phase 1.5 — GSC Enrichment Panel */}
      <GscEnrichmentPanel
        summary={summary}
        running={running || status?.status === "running"}
        onFilterByType={handleFilterByType}
        onEnrich={triggerEnrich}
      />

      {/* Top Priorities */}
      <TopPriorities issues={issues} />

      {/* Top Issue Types count cards */}
      {summary?.top_issue_types?.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {summary.top_issue_types.map((t) => (
            <button
              key={t.type}
              onClick={() => handleFilterByType(t.type)}
              className="bg-white border rounded-lg p-3 shadow-sm text-left hover:border-[#018D4C]/40 transition-colors"
            >
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                {ISSUE_TYPE_LABELS[t.type] || t.type}
              </div>
              <div className="text-lg font-bold mt-0.5">{t.count}</div>
            </button>
          ))}
        </div>
      )}

      {/* Issue Table */}
      <div ref={issueTableRef} className="bg-white border rounded-xl p-5 shadow-sm scroll-mt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-sm">Detected Issues</h2>
          <span className="text-xs text-muted-foreground">{issues.length} total</span>
        </div>
        <IssueTable
          issues={issues}
          onResolve={resolveIssue}
          onOpenDetail={(pageId) => navigate(`/dashboard/audit/page/${pageId}`)}
          sourceFilter={sourceFilter}
          externalTypeFilter={typeFilter}
        />
      </div>
    </div>
  );
}
